/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ProductPrice, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getSelectedProductIdFromActionInput } from '../themes/dobbies/actions/utilities/rating-utils';
import { getSelectedVariantAction, SelectedVariantInput } from '../themes/dobbies/actions/get-selected-variant.override.action';

/**
 * GetBuyboxActivePrices Input Action
 */
export class GetBuyboxActivePricesInput implements Msdyn365.IActionInput {
    public productId: number;
    // TODO: Construct the input needed to run the action
    constructor(productId: number) {
        this.productId = productId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `BUYBOX_ACTIVE_PRICES`;
    public getCacheObjectType = () => 'BUYBOX_ACTIVE_PRICES';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetBuyboxActivePricesData {
    prices: ProductPrice[];
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const productId = getSelectedProductIdFromActionInput(args);
    if (productId) {
        return new GetBuyboxActivePricesInput(+productId);
    } else {
        throw new Error('Unable to create GetBuyboxActivePricesInput, no productId found');
    }
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetBuyboxActivePricesInput, ctx: Msdyn365.IActionContext): Promise<IGetBuyboxActivePricesData> {
    const selectedVariantInput = new SelectedVariantInput(input.productId, ctx.requestContext.apiSettings.channelId);
    try {
        const product = await getSelectedVariantAction(selectedVariantInput, ctx);
        const priceAffiliations = [
            {
                AffiliationId: ctx.requestContext.app.config.AffiliationId,
                LoyaltyTierId: ctx.requestContext.app.config.LoyaltyTierId,
                AffiliationTypeValue: ctx.requestContext.app.config.AffiliationTypeValue,
                ReasonCodeLines: [],
                CustomerId: '',
                ExtensionProperties: []
            }
        ];

        const productRecId: number = product?.RecordId || 0;
        const projectDomain: ProjectionDomain = {
            ChannelId: ctx.requestContext.apiSettings.channelId,
            CatalogId: ctx.requestContext.apiSettings.catalogId
        };
        const prices = await getActivePricesAsync(
            { callerContext: ctx, queryResultSettings: {} },
            projectDomain,
            [productRecId],
            new Date(),
            null,
            priceAffiliations,
            true
        );
        return { prices };
    } catch (error) {
        if (error instanceof Error) {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug(`Failed to get buybox active prices: ${error}. Error executing action`);
            throw new Error(`Failed to get buybox active prices: ${error}`);
        }
        return { prices: [] };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetBuyboxActivePricesData>>action,
    // TODO: Give your data action a unique id
    id: 'GetBuyboxActivePrices',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
